import type { DayKey, OpeningHours, PhotoLayout, PublicOrganization, TimeRange } from './types';

export const DAY_KEYS: DayKey[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export function photoLayoutFor(count: number): PhotoLayout {
  if (count <= 0) return 'none';
  if (count < 3) return 'cover';
  return 'gallery';
}

// Team shows in auto mode with 2+ active providers; with one the page shows
// "About <name>" instead. Mirrors BookingPageSettings#show_team? for the
// admin live preview.
export function resolveShowTeam(mode: 'auto' | 'show' | 'hide', activeProviders: number): boolean {
  if (mode === 'show') return true;
  if (mode === 'hide') return false;
  return activeProviders >= 2;
}

export function formatRanges(ranges: TimeRange[]): string | null {
  if (ranges.length === 0) return null;
  return ranges.map((r) => `${r.from} – ${r.to}`).join(', ');
}

export interface HoursRow {
  days: DayKey[];
  label: string | null; // null = closed
}

// Groups consecutive days with the same hours: Mon–Fri 08:00 – 19:00.
export function groupHours(hours: OpeningHours): HoursRow[] {
  const rows: HoursRow[] = [];
  for (const { day, ranges } of hours.days) {
    const label = formatRanges(ranges);
    const last = rows[rows.length - 1];
    if (last && last.label === label) last.days.push(day);
    else rows.push({ days: [day], label });
  }
  return rows;
}

export function todayKey(date: Date = new Date(), timeZone?: string): DayKey {
  const weekday = new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone }).format(date).toLowerCase();
  return weekday.slice(0, 3) as DayKey;
}

export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase() || '?';
}

// Without photos the hero repeats the top bar's logo and name. It is only
// worth showing when it adds something: a tagline, opening hours or the
// online chip (the language chip and share button alone don't count).
export function heroHasDetails(organization: PublicOrganization) {
  const page = organization.booking_page;
  return !!page.tagline || !!page.hours || page.show_online;
}
